import React, { useEffect, useState } from 'react';
import { Trophy, Clock, Medal, List } from 'lucide-react';
import API_URL from '../../config';
import { useLocation } from 'react-router-dom';

export default function PageLiveResults() {
    const [currentTime, setCurrentTime] = useState(new Date());
    const [dbResults, setDbResults] = useState([]);
    const [loading, setLoading] = useState(true);

    const location = useLocation();
    const isTvMode = new URLSearchParams(location.search).get('tv') === 'true';

    // Global CSS override to allow `position: sticky` to work correctly.
    // By default, index.css sets `overflow-x: hidden` on body which entirely breaks sticky headers.
    useEffect(() => {
        document.body.style.overflowX = 'visible';
        document.body.style.overflowY = 'visible'; // just to be completely sure
        return () => {
            document.body.style.overflowX = '';
            document.body.style.overflowY = '';
        };
    }, []);

    // Clock
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    // Fetch data
    useEffect(() => {
        const fetchData = () => {
            const currentYear = new Date().getFullYear().toString();
            fetch(`${API_URL}/results?year=${currentYear}`)
                .then(r => r.json())
                .then(data => {
                    if (data.results) {
                        setDbResults(data.results);
                    } else if (Array.isArray(data)) {
                        setDbResults(data);
                    } else {
                        setDbResults([]);
                    }
                    setLoading(false);
                })
                .catch(() => {
                    setLoading(false);
                });
        };

        fetchData();
        const interval = setInterval(fetchData, 60000); // Refresh every 60 seconds
        return () => clearInterval(interval);
    }, []);

    // Auto-scroll logic for TV mode
    useEffect(() => {
        if (!isTvMode) return;

        let autoScrollInterval;
        const startAutoScroll = () => {
            autoScrollInterval = setInterval(() => {
                const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
                if (atBottom) {
                    clearInterval(autoScrollInterval);
                    setTimeout(() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        setTimeout(startAutoScroll, 2000);
                    }, 5000); // hold at bottom for 5 seconds
                } else {
                    window.scrollBy({ top: 2, behavior: 'instant' });
                }
            }, 30); // ~60fps scroll
        };

        const initialWait = setTimeout(startAutoScroll, 5000); // hold at top for 5s initially

        return () => {
            clearInterval(autoScrollInterval);
            clearTimeout(initialWait);
        };
    }, [isTvMode, dbResults]);

    const formatTime = (date) => {
        return date.toLocaleTimeString('sk-SK');
    };

    const medalStyle = (idx) => {
        if (idx === 0) return 'bg-yellow-400 text-yellow-900 border border-yellow-500 scale-110';
        if (idx === 1) return 'bg-gray-300 text-gray-700 border border-gray-400';
        if (idx === 2) return 'bg-orange-300 text-orange-900 border border-orange-400';
        return 'bg-blue-100 text-blue-800 border border-blue-200';
    };

    const getCategoryColor = (catName) => {
        if (!catName) return 'bg-gray-100 text-gray-800 border-gray-200';
        const name = catName.toLowerCase();
        if (name.includes('katamaran') || name.includes('raft') || name.includes('c4')) return 'bg-red-100 text-red-800 border-red-200';
        if (name.includes('mix')) return 'bg-pink-100 text-pink-800 border-pink-200';
        if (name.includes('ženy') || name.includes('zene')) return 'bg-rose-100 text-rose-800 border-rose-200';
        if (name.includes('sup') || name.includes('zapas')) return 'bg-purple-100 text-purple-800 border-purple-200';
        if (name.includes('k1') || name.includes('k2')) return 'bg-blue-100 text-blue-800 border-blue-200';
        if (name.includes('c1') || name.includes('c2')) return 'bg-emerald-100 text-emerald-800 border-emerald-200';
        if (name.includes('junio')) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
        
        const colors = [
            'bg-indigo-100 text-indigo-800 border-indigo-200',
            'bg-teal-100 text-teal-800 border-teal-200',
            'bg-cyan-100 text-cyan-800 border-cyan-200',
            'bg-orange-100 text-orange-800 border-orange-200',
            'bg-lime-100 text-lime-800 border-lime-200',
            'bg-sky-100 text-sky-800 border-sky-200',
        ];
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = catName.charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    };

    // Sort so "Hlavný maratón" is first/prominent
    const sortedResults = [...dbResults].sort((a, b) => {
        if (a.name.toLowerCase().includes('hlavný')) return -1;
        if (b.name.toLowerCase().includes('hlavný')) return 1;
        return 0;
    });

    const baseTextClasses = isTvMode ? 'text-2xl' : 'text-sm md:text-base';
    const titleClasses = isTvMode ? 'text-4xl' : 'text-2xl md:text-3xl';
    const mainTitleClasses = isTvMode ? 'text-5xl md:text-6xl' : 'text-3xl md:text-4xl';

    return (
        <div className={`min-h-screen bg-blue-950 font-sans selection:bg-yellow-400 selection:text-blue-900 pb-20 ${isTvMode ? 'pt-36' : 'pt-28'}`} style={{ fontFamily: 'Inter, sans-serif' }}>
            
            {/* Hide scrollbars completely for TV/Kiosk clean look */}
            <style>{`
                ::-webkit-scrollbar {
                    display: none;
                }
                * {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>

            {/* Fixed Header */}
            <header className={`fixed top-0 left-0 w-full z-50 bg-blue-900 border-b border-white/20 shadow-2xl transition-all flex items-center ${isTvMode ? 'h-32' : 'h-24'}`}>
                <div className="w-full px-4 sm:px-8 mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className={`rounded-full border-2 border-yellow-400 p-0.5 bg-white shrink-0 ${isTvMode ? 'w-20 h-20' : 'w-12 h-12'}`}>
                            <img src="https://klubmalydunaj.sk/wp-content/uploads/2020/02/logo-3.png" alt="Logo" className="w-full h-full rounded-full" />
                        </div>
                        <div>
                            <h1 className={`text-white font-black uppercase tracking-widest leading-tight ${isTvMode ? 'text-2xl' : 'text-sm md:text-lg'}`} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                Malodunajský <span className="text-yellow-400">Vodácky Maratón</span>
                            </h1>
                            <div className="flex items-center gap-2 mt-1">
                                <span className={`uppercase tracking-widest font-black text-red-500 animate-pulse flex items-center gap-2 ${isTvMode ? 'text-xl' : 'text-xs'}`}>
                                    <div className="w-2 h-2 rounded-full bg-red-500"></div> LIVE VÝSLEDKY
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 bg-black/30 px-6 py-3 rounded-2xl border border-white/10 shadow-inner">
                        <Clock className={`text-yellow-400 ${isTvMode ? 'w-10 h-10' : 'w-6 h-6'}`} />
                        <span className={`text-white font-black tabular-nums tracking-wider ${isTvMode ? 'text-5xl' : 'text-xl'}`} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            {formatTime(currentTime)}
                        </span>
                    </div>
                </div>
            </header>

            {/* Content */}
            <main className={`w-full px-4 sm:px-8 mx-auto mt-10 ${isTvMode ? 'max-w-none' : 'max-w-7xl'}`}>
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-40 gap-6">
                        <div className={`border-4 border-blue-900 border-t-yellow-400 rounded-full animate-spin ${isTvMode ? 'w-32 h-32' : 'w-16 h-16'}`} />
                        <p className={`text-white font-bold animate-pulse ${isTvMode ? 'text-4xl' : 'text-xl'}`}>Načítavam živé dáta...</p>
                    </div>
                ) : dbResults.length === 0 ? (
                    <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-3xl p-12 text-center text-white shadow-2xl">
                        <Trophy className={`mx-auto mb-6 opacity-50 ${isTvMode ? 'w-32 h-32' : 'w-16 h-16'}`} />
                        <h2 className={`font-black uppercase tracking-widest ${isTvMode ? 'text-5xl' : 'text-2xl'}`} style={{ fontFamily: 'Montserrat, sans-serif' }}>Čakáme na prvé výsledky...</h2>
                        <p className={`mt-4 font-medium text-white/70 ${isTvMode ? 'text-3xl' : 'text-lg'}`}>Výsledné časy sa tu zobrazia hneď po tom, ako posádky dorazia do cieľa<br />a ich výsledky budú úspešne spracované.</p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-12">
                        {sortedResults.map((marathonType) => {
                            const isHlavny = marathonType.name.toLowerCase().includes('hlavný');
                            
                            return (
                                <div key={marathonType.id} className={`${isHlavny ? 'order-first' : ''} mb-12`}>
                                    <div className={`sticky z-40 bg-blue-950 flex items-center gap-4 py-3 md:py-4 border-b border-white/10 ${isTvMode ? 'top-32 h-[100px]' : 'top-24 h-[72px]'}`}>
                                        <div className={`p-4 bg-yellow-400 rounded-2xl shadow-lg transform -skew-x-12`}>
                                            <Trophy className={`text-blue-900 transform skew-x-12 ${isTvMode ? 'w-12 h-12' : 'w-8 h-8'}`} />
                                        </div>
                                        <h3 className={`${isHlavny ? mainTitleClasses : titleClasses} font-black uppercase tracking-tighter text-white drop-shadow-md`} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                            {marathonType.name}
                                        </h3>
                                    </div>

                                    <div className="h-6"></div>

                                    {marathonType.overall && marathonType.overall.length > 0 ? (
                                        <div className="bg-white rounded-[2rem] shadow-2xl border border-gray-100">
                                            <div className="w-full">
                                                <table className="w-full text-left border-collapse">
                                                    <thead className={`sticky z-30 shadow-md ${isTvMode ? 'top-[228px]' : 'top-[168px]'}`}>
                                                        <tr className="bg-blue-900 text-yellow-400 font-black uppercase tracking-widest text-[10px] md:text-sm">
                                                            <th className={`px-2 sm:px-4 py-4 md:py-6 w-12 md:w-16 ${isTvMode ? 'text-2xl py-8 w-24' : ''}`}>Por.</th>
                                                            <th className={`px-2 sm:px-4 py-4 md:py-6 w-16 md:w-24 ${isTvMode ? 'text-2xl py-8 w-32' : ''}`}>Číslo</th>
                                                            <th className={`px-2 sm:px-4 py-4 md:py-6 w-28 md:w-40 ${isTvMode ? 'text-2xl py-8 w-[280px]' : ''}`}>Kategória</th>
                                                            <th className={`px-2 sm:px-4 py-4 md:py-6 ${isTvMode ? 'text-2xl py-8' : ''}`}>Posádka</th>
                                                            <th className={`px-2 sm:px-4 py-4 md:py-6 text-center ${isTvMode ? 'text-2xl py-8' : ''}`}>Umiest.</th>
                                                            <th className={`px-4 sm:px-8 py-4 md:py-6 text-right ${isTvMode ? 'text-2xl py-8' : ''}`}>Čas</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="text-gray-800">
                                                        {marathonType.overall.map((crew, i) => {
                                                            let catPlacement = null;
                                                            if (!marathonType.name.toLowerCase().includes('para')) {
                                                                const categoryObj = marathonType.categories?.find(c => c.id === crew.category?.id);
                                                                if (categoryObj && categoryObj.crews) {
                                                                    const index = categoryObj.crews.findIndex(c => c.id === crew.id);
                                                                    if (index >= 0 && index < 3) catPlacement = index;
                                                                }
                                                            }

                                                            return (
                                                            <tr key={crew.id} className="border-b border-gray-100 hover:bg-blue-50/50 transition-colors group">
                                                                <td className="px-2 sm:px-4 py-3 md:py-5">
                                                                    <div className={`rounded-full flex items-center justify-center font-black shadow-md ${medalStyle(i)} ${isTvMode ? 'w-16 h-16 text-3xl' : 'w-8 h-8 text-xs'}`}>
                                                                        {i + 1}
                                                                    </div>
                                                                </td>
                                                                <td className="px-2 sm:px-4 py-3 md:py-5 text-center sm:text-left">
                                                                    <span className={`bg-gray-100 px-2 sm:px-3 py-1 rounded font-black text-gray-500 tabular-nums border border-gray-200 ${isTvMode ? 'text-2xl px-4 py-2' : 'text-xs md:text-sm'}`}>
                                                                        #{crew.start_number || '---'}
                                                                    </span>
                                                                </td>
                                                                <td className="px-2 sm:px-4 py-3 md:py-5">
                                                                    <span className={`inline-block px-3 py-1 rounded-xl font-black uppercase tracking-wider border whitespace-nowrap ${isTvMode ? 'text-2xl px-5 py-2' : 'text-[10px] md:text-xs'} ${getCategoryColor(crew.category?.name)}`}>
                                                                        {crew.category?.name || '---'}
                                                                    </span>
                                                                </td>
                                                                <td className="px-2 sm:px-4 py-3 md:py-5">
                                                                    <span className={`font-black text-blue-900 tracking-tight ${isTvMode ? 'text-4xl leading-tight' : 'text-sm md:text-base'} whitespace-normal`}>
                                                                        {crew.participants?.map(p => p.name).join(', ') || 'Neznámi'}
                                                                    </span>
                                                                </td>
                                                                <td className="px-2 sm:px-4 py-3 md:py-5 text-center">
                                                                    {catPlacement !== null && (
                                                                        <div className="flex justify-center">
                                                                            <span className={`flex items-center gap-1 font-black px-2 py-1 rounded-lg border tabular-nums ${medalStyle(catPlacement).replace('scale-110', '')} ${isTvMode ? 'text-2xl' : 'text-[10px] md:text-xs'}`}>
                                                                                {catPlacement + 1}. <span className={`opacity-80 ${isTvMode ? 'text-lg' : 'text-[8px] md:text-[9px]'}`}>miesto</span>
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td className={`px-4 sm:px-8 py-3 md:py-5 text-right font-black text-blue-900 tabular-nums tracking-tighter whitespace-nowrap ${isTvMode ? 'text-5xl' : 'text-lg md:text-xl'}`}>
                                                                    {crew.race_time?.total_time || '---'}
                                                                </td>
                                                            </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className={`bg-white/5 border border-white/10 rounded-2xl p-8 text-center text-white/60 font-bold ${isTvMode ? 'text-3xl' : 'text-lg'}`}>
                                            Výsledné časy sa tu zobrazia, hneď ako budú zaznamenané a schválené.
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </main>

        </div>
    );
}
